import { useEffect, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import { logout } from '../store/auth.slice'
import { resetPayload } from '../store/form.slice'
import { Status } from './status'
import { Footer } from './footer'
import { Logo } from '../assets/logo'
import { getDirtySubmissionForm, isFormDirty } from '../utils/functions'
import { FormSchema } from '../utils/types/form'

export const Nav = () => {
  const [avatarName, setAvatarName] = useState('')
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  /* const [isNotificationsOpen, setIsNotificationsOpen] = useState(false) */
  const [showFormDraftIndicator, setShowFormDraftIndicator] = useState(false)
  const [dirtySubmissionForm, setDirtySubmissionForm] = useState<FormSchema | null>(null)
  const [isDraftContextMenuOpen, setIsDraftContextMenuOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [requestState, setRequestState] = useState({ error: '' })
  const profileMenuTriggerRef = useRef(null)
  const profileMenuContextRef = useRef(null)
  /* const notificationsTriggerRef = useRef(null)
  const notificationsContextRef = useRef(null) */
  const draftsTriggerRef = useRef(null)
  const draftsContextRef = useRef(null)

  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const formState = useAppSelector((state) => state.form)
  const { payload } = formState
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()

  const actionQuery = searchParams.get('action')
  const isPreviewingTemplate = actionQuery === '__previewTemplate'

  useOutsideClick({ element: profileMenuContextRef, trigger: profileMenuTriggerRef }, () =>
    setIsProfileMenuOpen(false)
  )

  /* useOutsideClick(
    { element: notificationsContextRef, trigger: notificationsTriggerRef },
    () => setIsNotificationsOpen(false)
  ) */

  useOutsideClick({ element: draftsContextRef, trigger: draftsTriggerRef }, () =>
    setIsDraftContextMenuOpen(false)
  )

  useEffect(() => {
    const handleBeforeUnload = (e: Event) => {
      const isDirtyNewForm = isFormDirty()
      const dirtySubmissionForm = getDirtySubmissionForm()

      if (isDirtyNewForm || dirtySubmissionForm) {
        e.preventDefault()
      } else {
        const draft = localStorage.getItem('draft')

        if (draft) {
          localStorage.removeItem('draft')
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    if (authUser) {
      const fullName = authUser.name ?? authUser.email
      const firstName = fullName.split(/\s/)[0]
      setAvatarName(firstName)
    }
  }, [authUser])

  useEffect(() => {
    window.scrollTo(0, 0)

    const isViewingEditForm =
      pathname.includes('edit') ||
      (pathname.includes('preview') && searchParams.get('edit') === '1')

    if (payload.meta?.isEditingForm && !isViewingEditForm) {
      dispatch(resetPayload())
    }

    const isViewingForm =
      pathname.includes('edit') || (pathname.includes('preview') && !isPreviewingTemplate)

    const showFormDraftIndicator =
      isFormDirty() && !(pathname.includes('new') ? isViewingForm : isViewingEditForm)

    setShowFormDraftIndicator(showFormDraftIndicator)

    const isSubmittingForm = pathname.includes('/submit') || pathname.includes('/review')
    const dirtySubmissionForm = getDirtySubmissionForm()
    const showSubmissionDraftIndicator = !!dirtySubmissionForm && !isSubmittingForm

    showSubmissionDraftIndicator
      ? setDirtySubmissionForm(dirtySubmissionForm)
      : setDirtySubmissionForm(null)
    // eslint-disable-next-line
  }, [pathname])

  const handleViewFormDraft = () => {
    setIsDraftContextMenuOpen(false)
    navigate(`/form/new/edit`, { viewTransition: true })
  }

  const handleViewSubmissionDraft = (formId: string) => {
    setIsDraftContextMenuOpen(false)
    navigate(`/form/${formId}/submit`, { viewTransition: true })
  }

  const handleViewProfile = () => {
    setIsProfileMenuOpen(false)
    navigate('/profile', { viewTransition: true })
  }

  const handleLogout = async () => {
    const isDirtyNewForm = isFormDirty()

    if (isDirtyNewForm && !window.confirm('Changes you made may not be saved. Continue?')) {
      return
    }

    try {
      setIsLoading(true)

      const { status } = await dispatch(logout()).unwrap()
      if (status === 200) {
        navigate('/log-in', { viewTransition: true })
        localStorage.clear()
      }
    } catch (error: any) {
      setRequestState({ error: error.message })
      setIsLoading(false)
    }
  }

  return (
    <main>
      <nav>
        <div>
          <Logo handler={() => navigate(authUser ? '/dashboard' : '/', { viewTransition: true })} />
          {authUser && (
            <div className='nav-actions'>
              <div className='nav-icon'>
                <div
                  id='draft_trigger'
                  onClick={() => setIsDraftContextMenuOpen(!isDraftContextMenuOpen)}
                  ref={draftsTriggerRef}
                  title={`${showFormDraftIndicator || dirtySubmissionForm ? 'You have drafts' : 'No drafts'}`}>
                  <span className='material-symbols-rounded'>draft</span>
                </div>
                {(showFormDraftIndicator || dirtySubmissionForm) && (
                  <span className='on-indicator'>●</span>
                )}
                {isDraftContextMenuOpen && (
                  <div className='context_menu draft_container' ref={draftsContextRef}>
                    {showFormDraftIndicator || dirtySubmissionForm ? (
                      <>
                        <h4>Drafts...</h4>
                        <div>
                          {showFormDraftIndicator && (
                            <div onClick={handleViewFormDraft}>
                              <h3>New form</h3>
                              <span className='material-symbols-rounded'>draft</span>
                              <span className='highlight-styled-hover'>{payload.form.name}</span>
                            </div>
                          )}
                          {dirtySubmissionForm && (
                            <div onClick={() => handleViewSubmissionDraft(dirtySubmissionForm._id)}>
                              <h3>New entry</h3>
                              <span className='material-symbols-rounded'>draft</span>
                              <span className='highlight-styled-hover'>
                                {dirtySubmissionForm.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <h4>You have no drafts</h4>
                        <div>
                          <span>
                            Your drafted work will appear here. Make any progress in creating a new
                            form or filling out an entry to see it in action.
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {/* <div className='nav-icon'>
                <span
                  id='notifications_trigger'
                  className='material-symbols-rounded'
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  ref={notificationsTriggerRef}>
                  notifications
                </span>
                {isNotificationsOpen && (
                  <div className='context_menu' ref={notificationsContextRef}>
                    {authUser?.notifications?.length ? (
                      <div
                        className='highlight highlight-blue'
                        onClick={() => {
                          navigate(`/admin/notifications/${authUser._id}`, {
                            viewTransition: true
                          })
                        }}>
                        <span className='bold'>
                          New notifications: {authUser.notifications.length}
                        </span>
                        <span className='span-a'>View</span>
                      </div>
                    ) : (
                      <span>No new notifications...</span>
                    )}
                  </div>
                )}
              </div> */}
              <div className='avatar'>
                <span
                  id='profile_menu_trigger'
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  ref={profileMenuTriggerRef}>
                  Hello {avatarName}
                </span>
                {isProfileMenuOpen && (
                  <div className='context_menu context_menu-wrap' ref={profileMenuContextRef}>
                    <span className='highlight-styled-hover' onClick={handleViewProfile}>
                      My account
                    </span>
                    <span className='highlight-styled-hover' onClick={handleLogout}>
                      {isLoading ? 'Logging' : 'Log'} out
                    </span>
                    <span>
                      <a
                        className='highlight-styled-hover'
                        href='mailto:andrewkagotho7@gmail.com?subject=Springforms:%20Feedback'>
                        Feedback
                      </a>
                    </span>
                    <div>
                      <em className='badge badge-orange'>Public beta</em>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
      {requestState.error && (
        <Status
          cause='Log out'
          error={requestState.error}
          reset={() => setRequestState({ ...requestState, error: '' })}
        />
      )}
      <Outlet />
      <Footer />
    </main>
  )
}
